import { HttpClient } from '@angular/common/http';
import { inject, Injectable, AfterViewInit } from '@angular/core';
import { SnackService } from './snack.service';
import { serverurl } from '../../environments/environment';
import { Track, UserTrack } from '../interfaces/trackdata';
import { UserdataService } from './userdata.service';
import { StepResult, TrackResult } from '../interfaces/responsedata.ts/trackresults';
import { BehaviorSubject } from 'rxjs';
import { TrackFile } from '../interfaces/responsedata.ts/trackfiles';

@Injectable({
  providedIn: 'root'
})
export class TrackService {
  // Injections
  http = inject(HttpClient);
  snack = inject(SnackService)
  user = inject(UserdataService);

  // Objects
  trackResults = new BehaviorSubject<TrackResult[]>([]);
  trackFiles = new BehaviorSubject<TrackFile[]>([]);
  userTracks = new BehaviorSubject<UserTrack[]>([]);


  async generateLpLink(roundId: string): Promise<string | false> {
    return await new Promise((resolve) => {
      this.http.post(`${serverurl}/streams/generatelplink`, { roundId }).subscribe({
        next: async (data: any) => {
          this.snack.success(data.msg);
          resolve(data.refId as string);
        },
        error: (err) => {
          this.snack.error(err);
          resolve(false);
        }
      });
    });
  }
  async getLpLinkId(roundId: string): Promise<string | false> {
    return await new Promise((resolve) => {
      this.http.get(`${serverurl}/streams/getlplink/${roundId}`).subscribe({
        next: async (data: any) => {
          resolve(data.refId as string);
        },
        error: (err) => {
          this.snack.error(err);
          resolve(false);
        }
      });
    });
  }

  async initialTrackDataLoad(trackId: string, roundId: string): Promise<false | { trackProcessData: Track[]; userProcessData: UserTrack[], userTrackFiles: any[] }> {
    const [trackResult, allUserStreams, trackFiles] = await Promise.all([
      // Load Track relevant Data (ProcessDat) by trackId
      // TrackData => StreamData => StepData
      await this.loadTracksAndStreamsProcessData(),
      // Load User TrackData
      // All Results related to the current track will be loaded by roundId
      await this.loadUserTrackData(),
      // Load UserFiles
      await this.loadTrackFilesByRoundId(roundId)
    ]);

    if (trackResult === false) {
      this.snack.errorText('Failed to load track process data.');
      return false;
    }

    if (allUserStreams === false) {
      this.snack.errorText('Failed to load user track data.');
      return false;
    }

    if (trackFiles === false) {
      this.snack.errorText('Failed to load user track files.');
      return false;
    }


    let results: UserTrack | undefined = await allUserStreams.find((userTrack: UserTrack) =>
      userTrack.trackId === trackId
      && userTrack.roundId === roundId);
    // console.log("Central loaded", allUserStreams, trackId);

    this.trackResults.next(results?.results || []);
    this.trackFiles.next(trackFiles)
    this.matchResultsToFile();
    return { trackProcessData: trackResult, userProcessData: allUserStreams, userTrackFiles: trackFiles }
  }
  async saveComHistory(stepId: string, roundId: string, communicationHistory: any) {
    let storage: any[] = []
    try {
      storage = JSON.parse(localStorage.getItem('comHistory_new') || '[]');
    } catch (error) {
      console.log(error);
      this.snack.errorText("Localstorage for Communicationhistory is invalid!");
      return;
    }
    let stepIdExistst = await storage.find(s => s.stepId == stepId && s.roundId == roundId)
    if (!stepIdExistst) {
      storage.push({ stepId, roundId, communicationHistory });
      localStorage.setItem('comHistory_new', JSON.stringify(storage));
      return;
    }
    for await (let s of storage) {
      if (s.stepId === stepId && s.roundId == roundId) {
        s.communicationHistory = communicationHistory;
        localStorage.setItem('comHistory_new', JSON.stringify(storage));
        return;
      }
    }
    console.log('Step not found or updated successfully');
  }
  async loadComHistory(stepId: string, roundId: string): Promise<any> {
    const storage: any[] = JSON.parse(localStorage.getItem('comHistory_new') || '[]');
    const stepData = await storage.find(s => s.stepId === stepId && s.roundId == roundId);
    return stepData ? stepData.communicationHistory : null;
  }
  async loadTracksAndStreamsProcessData(): Promise<Track[] | false> {
    return await new Promise((resolve) => {
      this.http.get(serverurl + '/data/tracks').subscribe({
        next: async (data: any) => {
          resolve(data as Track[]);
        },
        error: (err) => {
          this.snack.error(err);
          resolve(false);
        }
      });
    });
  }
  async loadUserTrackData(): Promise<UserTrack[] | false> {
    return new Promise((resolve) => {
      let date = new Date().getTime();
      this.http.get(serverurl + '/streams/usertracks?t=' + date).subscribe({
        next: (data: any) => {
          this.userTracks.next(data as UserTrack[])
          resolve(data as UserTrack[]);
        },
        error: (err) => {
          this.snack.error(err);
          resolve(false);
        }
      });
    });
  }
  async loadTrackFilesByRoundId(roundId: string): Promise<any[] | false> {
    return new Promise((resolve) => {
      let date = new Date().getTime();
      this.http.get(serverurl + `/knowledgehub/trackresult/${roundId}?t=${date}`).subscribe({
        next: async (data: any) => {
          this.trackFiles.next(data)
          resolve(data);
        },
        error: (err) => {
          this.snack.error(err);
          resolve(false);
        }
      });
    });
  }
  async updateTrackProgress(roundId: string, streamId: string, stepId: string) {
    return new Promise((resolve) => {
      this.http.put(serverurl + '/streams/usertracks', { streamId, stepId, roundId, result: this.trackResults.getValue() }).subscribe({
        next: (data: any) => {
          resolve(true);
        },
        error: (err) => {
          console.log('Error updating stream progress:', err);
          this.snack.error(err);
          resolve(false);
        }
      });
    });
  }
  async matchResultsToFile() {
    let oldResult = this.trackResults.getValue();
    for await (let t of oldResult) {
      for await (let res of t.stepResults) {
        if (res.fileId) {
          res.file = await this.trackFiles.getValue().find((f: TrackFile) => f.id === res.fileId)
          if (!res.file) {
            res.fileId = undefined;
          }
        }
      }
    }
    this.trackResults.next(oldResult);
  }
  async createFileFromResultData(roundId: string, name: string, data: any, type: string, stepId: string, streamId: string): Promise<any | boolean> {
    let convertedTyper = type.toLowerCase().replaceAll(' ', '_');
    return new Promise(async (resolve) => {

      let currentProjectId = await this.user.getDefaultProjectId() || "";
      if (!currentProjectId || currentProjectId === "") {
        resolve(false);
        return;
      }

      this.http.post(serverurl + '/knowledgehub/trackresult', { roundId, name, data, type: convertedTyper, projectId: currentProjectId, stepId: stepId }).subscribe({
        next: async (responsedata: any) => {
          let oldResults = this.trackResults.getValue();
          for await (let result of oldResults) {
            if (result.streamId === streamId) {

              for await (let res of result.stepResults) {
                if (res.stepId === stepId) {
                  res.fileId = responsedata.filedata.id;
                  res.result = data
                }
              }
            }
          }
          await this.trackResults.next(oldResults);
          await this.updateTrackProgress(roundId, streamId, stepId);
          await this.loadTrackFilesByRoundId(roundId);
          this.matchResultsToFile();
          resolve(responsedata.filedata);
        },
        error: (err) => {
          console.log(err);

          this.snack.error(err);
          resolve(false);
        }
      });
    });
  }
  async downloadTrackResultFile(roundId: string, fileId: string, fileName: string,) {
    return new Promise((resolve) => {
      let date = new Date().getTime();
      this.http.get(`${serverurl}/knowledgehub/trackresult/${roundId}/${fileId}/${fileName}?t=${date}`, { responseType: 'blob' }).subscribe({
        next: async (data: any) => {
          const blob = new Blob([data], { type: 'application/octet-stream' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          a.click();
          window.URL.revokeObjectURL(url);
          resolve(true);
        },
        error: (err) => {
          this.snack.error(err);
          resolve(false);
        }

      });
    });
  }
  async deleteFileFromTrackResult(roundId: string, id: string, fileName: string) {
    // Inclidung delete TrackResult
    return new Promise((resolve) => {
      this.http.delete(`${serverurl}/knowledgehub/trackresult/${roundId}/${id}/${fileName}`).subscribe({
        next: async (data: any) => {
          let oldResults = await this.trackResults.getValue();
          for await (let result of oldResults) {
            result.stepResults = await result.stepResults.filter((r) => r.fileId !== id);
          }
          await this.loadTrackFilesByRoundId(roundId);
          this.trackResults.next(oldResults);
          this.matchResultsToFile();
          resolve(true);
        },
        error: (err) => {
          this.snack.error(err);
          resolve(false);
        }
      });
    });
  }
  async updateTrackName(roundId: string, name: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.http.put(serverurl + '/streams/trackname', { roundId, name }).subscribe({
        next: async (data: any) => {
          resolve(true);
        },
        error: (err) => {
          this.snack.error(err);
          resolve(false);
        }
      });
    });
  }
  async addStepResult(roundId: string, streamId: string, stepId: string, result: { [key: string]: any }, stepName: string): Promise<boolean> {

    const resultObject: StepResult = await {
      result,
      stepId,
      name: stepName,
      finished: false,
      fileId: undefined
    }

    let oldTrackResults: TrackResult[] = this.trackResults.getValue();
    if (oldTrackResults && oldTrackResults.length > 0) {

      console.log("TCL: ")
      // return false;
    }


    let streamExists: boolean = false;
    let stepExists: boolean = false;
    for await (let re of oldTrackResults) {
      if (re.streamId === streamId) {
        streamExists = true;
        re.stepResults = await re.stepResults.filter((ste) => ste.stepId !== stepId)
      }
    }

    // if Step NOT exists but Stream
    if (streamExists === true && stepExists === false) {
      for await (let re of oldTrackResults) {
        if (re.streamId === streamId) {
          re.stepResults.push(resultObject);
        }
      }
    }
    // if stream AND step not exists
    if (streamExists === false && stepExists === false) {
      oldTrackResults.push({
        streamId: streamId,
        stepResults: [resultObject]
      });
    }

    await this.trackResults.next(oldTrackResults);
    await this.updateTrackProgress(roundId, streamId, stepId);
    return true
  }
  async saveResult(roundId: string, streamId: string, result: StepResult): Promise<boolean> {
    return await new Promise((resolve) => {
      this.http.put(serverurl + '/streams/usertracks/result', { roundId, result, streamId }).subscribe({
        next: (data: any) => {
          resolve(true);
        },
        error: (err) => {
          this.snack.error(err);
          resolve(false);
        }
      });
    });
  }
  async deleteTrack(roundId: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.http.post(serverurl + '/streams/deletetrack', { roundId }).subscribe({
        next: async (data: any) => {
          let d = await this.trackResults.getValue();
          d = d.filter((t: any) => t.roundId !== roundId);
          this.trackResults.next(d);

          this.userTracks.next(this.userTracks.getValue().filter((track: UserTrack) => track.roundId !== roundId));

          try {
            let storage = JSON.parse(localStorage.getItem('comHistory_new') || '[]');
            storage = storage.filter((item: any) => item.roundId !== roundId);
            localStorage.setItem('comHistory_new', JSON.stringify(storage));
          } catch (error) {
            console.log('PARSING deleteTrack', error);

          }
          resolve(true);
        },
        error: (err) => {
          this.snack.error(err);
          resolve(false);
        }
      });
    });
  }
  async startTrack(trackId: string, streamId: string, stepId: string, projectId: string, name: string | null): Promise<false | string> {
    return await new Promise((resolve) => {
      this.http.post(serverurl + '/streams/starttrack', { trackId, streamId, stepId, projectId, name }).subscribe({
        next: (data: any) => {
          this.loadUserTrackData();
          resolve(data.roundId);
        },
        error: (err) => {
          this.snack.error(err);
          resolve(false);
        }
      });
    });
  }
}
